/**
 * ThemeProvider — global theme state. Reads/writes `friday:theme` in
 * localStorage and sets `data-theme` on <html>; the matching rule in
 * themes.css paints the tokens. Exposes a `setTheme(id)` and the current
 * `theme` via context so any component can read it.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  THEMES,
  DEFAULT_THEME_ID,
  DEFAULT_FAVICON,
  resolveThemeId,
  tokenToHex,
  type ThemeDefinition,
} from './themes'

interface ThemeContextValue {
  theme: ThemeDefinition
  setTheme: (id: string) => void
  themes: ThemeDefinition[]
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'friday:theme'

function readPersisted(): string {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID
  try {
    const v = resolveThemeId(window.localStorage.getItem(STORAGE_KEY))
    if (v) return v
  } catch {
    // ignore — localStorage may be unavailable (private mode, quota)
  }
  return DEFAULT_THEME_ID
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeId] = useState<string>(() => readPersisted())

  // Apply tokens by switching data-theme; the matching CSS rule in
  // themes.css paints the variables.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.dataset.theme = themeId
    // The browser chrome colour and the tab icon follow the theme.
    const def = THEMES.find((t) => t.id === themeId)
    if (def) {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', tokenToHex(def.tokens.bg))
      document.querySelector('link[rel="icon"]')?.setAttribute('href', def.favicon ?? DEFAULT_FAVICON)
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, themeId)
    } catch {
      // ignore
    }
  }, [themeId])

  const setTheme = useCallback((id: string) => {
    if (THEMES.some((t) => t.id === id)) setThemeId(id)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ theme: THEMES.find((t) => t.id === themeId) ?? THEMES[0]!, setTheme, themes: THEMES }),
    [themeId, setTheme],
  )
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * The theme id outside a provider is the default; used by components (the
 * wordmark) that also render on pages that may mount without the provider.
 */
export function useThemeId(): string {
  return useContext(ThemeContext)?.theme.id ?? DEFAULT_THEME_ID
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}